import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Plus, ChevronRight, Calendar, Trash2 } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { usePlanillas, useDeletePlanilla } from '../../hooks/usePlanillas';
import { useAuthStore } from '../../stores/auth-store';

const ESTADO_FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'borrador', label: 'Borrador' },
  { value: 'pendiente_compras', label: 'Pend. Compras' },
  { value: 'pendiente_administrativo', label: 'Pend. Admin' },
  { value: 'correccion_solicitada', label: 'Corrección' },
  { value: 'revisada', label: 'Revisada' },
  { value: 'aprobada', label: 'Aprobada' },
  { value: 'pagada', label: 'Pagada' },
];

export function PlanillasProjectPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [estadoFilter, setEstadoFilter] = useState('');
  const deleteMutation = useDeletePlanilla();

  const { data, isLoading, error } = usePlanillas(projectId!, {
    estado: estadoFilter || undefined,
  });

  const planillasList = data?.data ?? [];
  const userRole = user?.role ?? '';
  const canCreatePlanilla = ['residente', 'gerente', 'operaciones'].includes(userRole);
  const hasBorrador = planillasList.some((p) => p.estado === 'borrador');
  const canDeleteBorrador = ['residente', 'administrativo', 'gerente', 'operaciones'].includes(userRole);

  const navigateToPlanilla = useCallback((planillaId: string, estado: string) => {
    if (userRole === 'compras' && estado === 'pendiente_compras') {
      navigate(`/compras/planillas/${planillaId}/descuentos`);
    } else if ((userRole === 'gerente' || userRole === 'operaciones') && estado === 'revisada') {
      navigate(`/planillas/${planillaId}/aprobacion`);
    } else if (userRole === 'administrativo' && estado === 'pendiente_administrativo') {
      navigate(`/planillas/${planillaId}/revision`);
    } else {
      navigate(`/proyectos/${projectId}/planillas/${planillaId}`);
    }
  }, [navigate, userRole, projectId]);

  if (isLoading) {
    return (
      <div className="space-y-4 fade-up">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600 mb-2">Error al cargar planillas</p>
        <Button variant="secondary" onClick={() => window.location.reload()}>Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-brand-dark flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Planillas
        </h1>
        {canCreatePlanilla && !hasBorrador && (
          <Button size="sm" onClick={() => navigate(`/proyectos/${projectId}/planillas/nueva`)}>
            <Plus className="w-4 h-4 mr-1" />
            Nueva Planilla
          </Button>
        )}
      </div>

      {/* Status filter */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {ESTADO_FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-full font-medium transition ${
              estadoFilter === opt.value
                ? 'bg-brand-dark text-white'
                : 'bg-white/60 text-gray-600 hover:bg-white'
            }`}
            onClick={() => setEstadoFilter(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* List */}
      {planillasList.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No hay planillas registradas</p>
          {canCreatePlanilla ? (
            <Button size="sm" onClick={() => navigate(`/proyectos/${projectId}/planillas/nueva`)}>
              <Plus className="w-4 h-4 mr-1" />
              Crear primera planilla
            </Button>
          ) : (
            <p className="text-xs text-gray-400 mt-1">
              No hay planillas registradas para este proyecto.
            </p>
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {planillasList.map((p) => (
            <div
              key={p.id}
              className="cursor-pointer"
              onClick={() => navigateToPlanilla(p.id, p.estado)}
            >
              <Card className="p-4 hover:shadow-md transition">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-800">
                      Planilla N°{p.numero}
                    </p>
                    <StatusBadge entity="planilla" status={p.estado} />
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>{p.fecha_desde} al {p.fecha_hasta}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{p.created_by.nombre}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-brand-dark">
                    ${p.total_neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  {canDeleteBorrador && p.estado === 'borrador' && (
                    <button
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Eliminar planilla"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm('¿Eliminar esta planilla? Esta acción no se puede deshacer.')) {
                          await deleteMutation.mutateAsync(p.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
